import React, {
  ReactElement,
  SyntheticEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  ArrowSmallDownIcon,
  ArrowSmallUpIcon,
  XMarkIcon,
} from '@heroicons/react/20/solid';
import { Disclosure } from '@headlessui/react';

import {
  Button,
  Select,
  TextField,
  DateRangePicker,
  inputLabelClasses,
  MultiSelectCheckBox,
  MultiSelectInputType,
} from 'src/ui/components';
import { OptionItem } from 'src/ui/components';
import SearchableGroupedSelect, {
  AccountOption,
} from 'src/ui/components/SearchableGroupedSelect';

import { useAuth } from 'src/hooks';
import { useAllAccountsInfo } from '../api/useAllAccountsInfo';

import {
  activities,
  activityTypes,
  activityTypeToActivitiesMap,
  sourceList,
  tabActivityOptions,
} from '../utils/activityFilterConstants';
import { classNames } from 'src/utils/className';

import { ActivityLogFilterType } from '../types/filterOptionsType';

type ActivityLogFilterPageType = {
  filterOptions?: ActivityLogFilterType;
  setValues: (type: keyof ActivityLogFilterType, value: string) => void;
  resetFilters: () => void;
  selectedToggleValue: string;
  sortOptions: OptionItem[];
  setCategory: (value: Array<MultiSelectInputType>) => void;
  setCategoryActivity: (value: Array<MultiSelectInputType>) => void;
  setTabActivity: (value: Array<MultiSelectInputType>) => void;
  selected: AccountOption[];
  setSelected: (selected: AccountOption[]) => void;
  setSource: (value: Array<MultiSelectInputType>) => void;
};

function ActivityLogFilter({
  filterOptions,
  setValues,
  resetFilters,
  selectedToggleValue,
  sortOptions,
  setCategory,
  setCategoryActivity,
  setTabActivity,
  selected,
  setSelected,
  setSource,
}: ActivityLogFilterPageType): ReactElement {
  const { isExternalUser } = useAuth();

  const { data: allAccounts, isLoading: isAllAccountsLoading } =
    useAllAccountsInfo();

  const selectedByTabRef = useRef<any>({
    callDocuments: [],
    all: [],
  });

  const prevToggleRef = useRef<'callDocuments' | 'all' | null>(null);

  useEffect(() => {
    const prev = prevToggleRef.current;
    const current = selectedToggleValue as 'callDocuments' | 'all';

    if (prev === current) return;

    // run only after selected accounts are restored
    if (!hasInitializedSelected.current) {
      prevToggleRef.current = current;
      return;
    }

    // save previous tab selection
    if (prev) {
      selectedByTabRef.current[prev] = selected;
    }

    // restore current tab selection
    const restored = selectedByTabRef.current[current] || [];
    setSelected(restored);

    setValues(
      'accountCidPid',
      restored.length
        ? JSON.stringify(restored.map((s: AccountOption) => s.value))
        : '',
    );

    prevToggleRef.current = current;
  }, [selected, selectedToggleValue, setSelected, setValues]);

  const hasInitializedSelected = useRef(false);

  // sync selected state from filterOptions.accountCidPid only on mount (reload)
  useEffect(() => {
    if (hasInitializedSelected.current) return;

    const saved = window.localStorage.getItem('ActivityLogFilter');
    let raw: string | undefined = undefined;

    if (saved) {
      try {
        const parsed = JSON.parse(saved) as Partial<ActivityLogFilterType>;
        raw =
          typeof parsed?.accountCidPid === 'string'
            ? parsed.accountCidPid
            : undefined;
      } catch {
        raw = undefined;
      }
    }

    if (!raw && typeof filterOptions?.accountCidPid === 'string') {
      raw = filterOptions.accountCidPid;
    }

    let accountIds: number[] = [];
    if (typeof raw === 'string' && raw.length) {
      try {
        const parsedRaw = JSON.parse(raw);
        if (Array.isArray(parsedRaw)) {
          accountIds = parsedRaw
            .map((v: any) => Number(v))
            .filter(n => !Number.isNaN(n));
        } else if (typeof parsedRaw === 'number') {
          accountIds = [parsedRaw];
        } else if (typeof parsedRaw === 'string') {
          accountIds = parsedRaw
            .split(',')
            .map(s => Number(s.trim()))
            .filter(n => !Number.isNaN(n));
        }
      } catch {
        accountIds = raw
          .split(',')
          .map(s => Number(s.trim()))
          .filter(n => !Number.isNaN(n));
      }
    }

    if (accountIds.length) {
      const selectedOptions = (allAccounts?.Data ?? [])
        .filter((acc: any) => accountIds.includes(acc.AccountId))
        .map((acc: any) => ({
          label: `${acc.CID}-${acc.SID}-${acc.PID} ${acc.Name}`,
          value: acc.AccountId,
        }));

      const missing = accountIds.filter(
        id => !selectedOptions.some((s: any) => s.value === id),
      );
      const missingOptions = missing.map(id => ({
        label: String(id),
        value: id,
      }));

      setSelected([...selectedOptions, ...missingOptions]);
    } else {
      setSelected([]);
    }

    hasInitializedSelected.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedTypeValues = useMemo(
    () => filterOptions?.type?.map(t => t.value) ?? [],
    [filterOptions?.type],
  );

  const filteredActivities = useMemo(() => {
    const allowedActivityIds = new Set(
      selectedTypeValues.flatMap(
        type => activityTypeToActivitiesMap[type] ?? [],
      ),
    );
    const selectAllOption = activities.find(a => a.value === 0);

    const filtered = activities.filter(activity =>
      allowedActivityIds.has(activity.value),
    );

    if (selectAllOption && !filtered.find(a => a.value === 0)) {
      return [selectAllOption, ...filtered];
    }

    return filtered;
  }, [selectedTypeValues]);

  const filteredValues = useMemo(() => {
    if (!filteredActivities || !filterOptions?.categoryActivity) return [];

    const filteredIds = filteredActivities.map(a => a.value);

    return filterOptions.categoryActivity.filter(val =>
      typeof val === 'number'
        ? filteredIds.includes(val)
        : filteredIds.includes(val?.value),
    );
  }, [filteredActivities, filterOptions?.categoryActivity]);

  function renderSort(): ReactElement {
    return (
      <div className="flex items-end gap-2">
        <Select
          inputSize="small"
          label="Sort by"
          id="filter-status"
          className="w-full sm:w-auto"
          options={sortOptions}
          value={filterOptions?.sortType}
          onChange={(e: SyntheticEvent<HTMLSelectElement>) =>
            setValues('sortType', e.currentTarget.value)
          }
        />
        <Button
          variant={filterOptions?.sortOrder === 'asc' ? 'primary' : 'secondary'}
          size="small"
          onClick={() => setValues('sortOrder', 'asc')}
        >
          <ArrowSmallUpIcon className="h-5 w-5" aria-hidden="true" />
          <span className="sr-only">Sort in ascending order</span>
        </Button>
        <Button
          variant={
            filterOptions?.sortOrder === 'desc' ? 'primary' : 'secondary'
          }
          size="small"
          onClick={() => setValues('sortOrder', 'desc')}
        >
          <ArrowSmallDownIcon className="h-5 w-5" aria-hidden="true" />
          <span className="sr-only">Sort in descending order</span>
        </Button>
      </div>
    );
  }

  function renderDatePicker({
    id,
    key,
    value,
    title,
  }: {
    id: string;
    title: string;
    key?: string;
    value?: string;
  }): ReactElement {
    return (
      <DateRangePicker
        rangeType="past"
        format="MM/DD/YYYY HH:mm:ss"
        size="small"
        placeholder={title}
        id={id}
        value={value?.length ? value : []}
        setValues={setValues}
        keyValue={key}
      />
    );
  }

  function renderTextField({
    label,
    id,
    value,
  }: {
    label: string;
    id: keyof ActivityLogFilterType;
    value: any;
  }) {
    return (
      <TextField
        inputSize="small"
        className="w-full sm:w-auto"
        type="search"
        label={label}
        placeholder={label}
        id={id}
        value={value}
        onChange={e => setValues(id, e.target.value)}
      />
    );
  }

  function renderMultiSelectCheckbox({
    options,
    setValues,
    values,
    label,
  }: {
    options: Array<{ value: number; label: string | undefined }>;
    setValues: (values: Array<MultiSelectInputType>) => void;
    values: Array<MultiSelectInputType> | undefined;
    label?: string;
  }): ReactElement {
    return (
      <MultiSelectCheckBox
        options={options}
        setSelectedOptions={setValues}
        selectedOptions={values}
        size="small"
        id={label?.toLocaleLowerCase().replaceAll(' ', '')}
      />
    );
  }

  return (
    <Disclosure.Panel
      as="div"
      className="fixed bottom-0 left-0 top-0 z-10 flex w-full flex-col bg-white shadow dark:bg-neutral-800 sm:static sm:bottom-auto sm:left-auto sm:top-auto sm:block sm:w-auto"
    >
      {({ close }) => (
        <>
          <div className="flex items-center border-b border-b-light-light py-1 pe-1 ps-2 dark:border-b-dark-medium sm:hidden">
            <h3 className="grow text-lg font-medium">Filter</h3>
            <Button
              variant="subtle"
              onClick={() => {
                close();
              }}
            >
              <XMarkIcon className="h-5 w-5" />
            </Button>
          </div>
          <div className="min-h-0 grow overflow-y-auto p-2 pb-3 sm:overflow-y-visible sm:px-3 lg:px-4">
            {renderSort()}
            <div
              className="my-4 border-b border-gray-200 dark:border-neutral-700/50"
              aria-hidden="true"
            ></div>
            <div className="flex flex-wrap gap-3">
              <div className="flex w-full flex-col sm:w-auto">
                <label
                  htmlFor=""
                  className="block text-xs font-medium leading-6 text-gray-900 dark:text-white"
                >
                  Activity date
                </label>
                {renderDatePicker({
                  id: 'createdOn',
                  title: 'Activity date',
                  key: 'createdOn',
                  value: filterOptions?.createdOn,
                })}
              </div>
              {selectedToggleValue !== 'callDocuments' &&
                selectedToggleValue !== 'all' &&
                !isExternalUser &&
                renderTextField({
                  label: 'Account CID - PID',
                  id: 'accountCidPid',
                  value: filterOptions?.accountCidPid,
                })}
              {(selectedToggleValue === 'callDocuments' ||
                selectedToggleValue === 'all') &&
                !isExternalUser && (
                  <div className="flex w-full flex-col sm:w-auto">
                    <label
                      htmlFor="activity-type"
                      className={classNames(inputLabelClasses, 'text-xs')}
                    >
                      Account
                    </label>
                    <SearchableGroupedSelect
                      key={selectedToggleValue}
                      options={
                        allAccounts?.Data?.map((acc: any) => ({
                          label: `${acc.CID}-${acc.SID}-${acc.PID} ${acc.Name}`,
                          value: acc.AccountId,
                        })) || []
                      }
                      isLoading={isAllAccountsLoading}
                      selectedValues={selected}
                      onChange={newSelected => {
                        setSelected(newSelected);

                        const isAllSelected =
                          newSelected.length === allAccounts?.Data?.length;

                        setValues(
                          'accountCidPid',
                          isAllSelected
                            ? ''
                            : newSelected.length
                            ? JSON.stringify(newSelected.map(s => s.value))
                            : '',
                        );
                      }}
                    />
                  </div>
                )}

              {isExternalUser && (
                <>
                  {renderTextField({
                    label: 'CID',
                    id: 'cid',
                    value: filterOptions?.cid,
                  })}
                  {renderTextField({
                    label: 'PID',
                    id: 'pid',
                    value: filterOptions?.pid,
                  })}
                </>
              )}

              {selectedToggleValue === 'personAlerts' &&
                renderTextField({
                  label: 'Alert Number',
                  id: 'personAlertId',
                  value: filterOptions?.personAlertId,
                })}
              {selectedToggleValue === 'personRepository' &&
                renderTextField({
                  label: 'Person ID',
                  id: 'personId',
                  value: filterOptions?.personId,
                })}
              {selectedToggleValue === 'hospitalForm' &&
                renderTextField({
                  label: 'Submission Number',
                  id: 'submissionNumber',
                  value: filterOptions?.submissionNumber,
                })}

              {(selectedToggleValue === 'callDocuments' ||
                selectedToggleValue === '') &&
                renderTextField({
                  label: 'File name',
                  id: 'reportFileName',
                  value: filterOptions?.reportFileName,
                })}
              {selectedToggleValue !== 'all' &&
                tabActivityOptions[selectedToggleValue] && (
                  <div className="flex w-full flex-col sm:w-auto">
                    <label
                      htmlFor="tab-activity"
                      className={classNames(inputLabelClasses, 'text-xs')}
                    >
                      Activity
                    </label>
                    {renderMultiSelectCheckbox({
                      options: tabActivityOptions[selectedToggleValue],
                      setValues: setTabActivity,
                      values: filterOptions?.tabActivity,
                      label: 'Activity',
                    })}
                  </div>
                )}

              {selectedToggleValue === 'all' && (
                <>
                  <div className="flex w-full flex-col sm:w-auto">
                    <label
                      htmlFor="activity-type"
                      className={classNames(inputLabelClasses, 'text-xs')}
                    >
                      Type
                    </label>

                    {renderMultiSelectCheckbox({
                      options: activityTypes ?? [],
                      setValues: setCategory,
                      values: filterOptions?.type,
                      label: 'Type',
                    })}
                  </div>

                  <div className="flex w-full flex-col sm:w-auto">
                    <label
                      htmlFor="activity-type"
                      className={classNames(inputLabelClasses, 'text-xs')}
                    >
                      Activity
                    </label>
                    {renderMultiSelectCheckbox({
                      options: filteredActivities ?? [],
                      setValues: setCategoryActivity,
                      // values: filterOptions?.categoryActivity,
                      values:
                        filteredActivities.length === 0
                          ? undefined
                          : filteredValues,
                      label: 'Activity',
                    })}
                  </div>

                  {renderTextField({
                    label: 'ID',
                    id: 'docId',
                    value: filterOptions?.docId,
                  })}
                </>
              )}

              {renderTextField({
                label: 'User',
                id: 'userName',
                value: filterOptions?.userName,
              })}

              {isExternalUser && (
                <div className="flex w-full flex-col sm:w-auto">
                  <label
                    htmlFor="activity-type"
                    className={classNames(inputLabelClasses, 'text-xs')}
                  >
                    Source
                  </label>
                  {renderMultiSelectCheckbox({
                    options: sourceList ?? [],
                    setValues: setSource,
                    values: filterOptions?.source,
                    label: 'Source',
                  })}
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-col gap-3 border-t border-t-light-light p-2 dark:border-b-dark-medium sm:hidden">
            <Button
              className="grow"
              variant="link"
              onClick={() => {
                resetFilters();
              }}
            >
              Reset
            </Button>
            <div className="flex gap-1">
              <Button
                className="grow"
                onClick={() => {
                  close();
                }}
              >
                Cancel
              </Button>
              <Button
                className="grow"
                variant="primary"
                onClick={() => {
                  close();
                }}
              >
                Apply
              </Button>
            </div>
          </div>
        </>
      )}
    </Disclosure.Panel>
  );
}

export { ActivityLogFilter };
